import React, { useMemo } from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';

import { ChartWrapper } from 'src/components';
import { transparentize } from 'src/utils/util';
import { formatNumber } from 'src/utils/formatters';


const useStyles = makeStyles(theme => ({
  chartContainer: {
    height: '320px',
    width: '100%',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
  subtitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1,
  },
}));

const GeospatialChart = ({ allAssetsByType, currentGeoInfoTypeId }) => {
  const classes = useStyles();

  const assetConfigs = {
    gpp: {
      title: 'Productividad Primaria Bruta',
      color: '#43A047',
      type: 'bands',
    },
    albedo: {
      title: 'Albedo',
      color: '#FF7043',
      type: 'bands',
    },
    ndti: {
      title: 'NDTI',
      color: '#5E35B1',
      type: 'bands',
    },
    ndmi: {
      title: 'NDMI',
      color: '#039BE5',
      type: 'bands',
    },
    si: {
      title: 'Índice de Salinidad',
      color: '#60e7ffff',
      type: 'bands',
    },
    richness: {
      title: 'Riqueza',
      color: '#4CAF50',
      type: 'bands',
    },
    'landcover-water': {
      title: 'Superficie',
      type: 'multi', // Gráfico de múltiples líneas
      lines: {
        water: { label: 'Agua', color: '#2196F3' },
        snow: { label: 'Nieve', color: '#93dfe6' },
        total: { label: 'Total', color: '#081d58' },
      },
    },
    'eii-reclass': {
      title: 'Superficie (ha)',
      type: 'multi', // Gráfico de múltiples líneas
      lines: {
        veryLow: { label: 'Muy Bajo', color: 'rgb(255,215,0)' },
        low: { label: 'Bajo', color: 'rgb(50,205,50)' },
        medium: { label: 'Medio', color: 'rgb(0,128,0)' },
        high: { label: 'Alto', color: 'rgb(220,20,60)' },
        veryHigh: { label: 'Muy Alto', color: 'rgb(255,140,0)' },
      },
    },
    'conversion-areas': {
      title: 'Superficie convertida (ha)',
      color: '#FF5722',
      type: 'simple',
    },
  };

  const currentConfig = assetConfigs[currentGeoInfoTypeId];

  // Preparar datos para el gráfico
  const chartData = useMemo(() => {
    // Solo mostrar el gráfico si está configurado para este tipo
    if (!currentConfig) {
      return null;
    }

    const currentTypeAssets = allAssetsByType[currentGeoInfoTypeId] || [];

    if (currentTypeAssets.length === 0) {
      return null;
    }

    if (currentConfig.type === 'bands') {

      const assetsWithStats = currentTypeAssets
        .filter(asset => asset.stats &&
          typeof asset.stats.mean === 'number' &&
          typeof asset.stats.stdDev === 'number')
        .sort((a, b) => a.year - b.year);

      if (assetsWithStats.length === 0) {
        return null;
      }

      const years = assetsWithStats.map(asset => asset.stats.year || asset.year);
      const meanValues = assetsWithStats.map(asset => asset.stats.mean);
      const upperBandValues = assetsWithStats.map(asset => asset.stats.mean + asset.stats.stdDev);
      const lowerBandValues = assetsWithStats.map(asset => asset.stats.mean - asset.stats.stdDev);

      const color = currentConfig.color;

      return {
        labels: years,
        datasets: [
          {
            label: 'Banda Inferior (-1σ)',
            data: lowerBandValues,
            borderColor: transparentize(color, 0.7),
            backgroundColor: 'transparent',
            borderWidth: 1,
            fill: false,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 0,
          },
          {
            label: 'Banda Superior (+1σ)',
            data: upperBandValues,
            borderColor: transparentize(color, 0.8),
            backgroundColor: transparentize(color, 0.7),
            borderWidth: 1,
            fill: '-1',
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 0,
          },
          {
            label: 'Media',
            data: meanValues,
            borderColor: color,
            backgroundColor: transparentize(color, 0.4),
            borderWidth: 3,
            fill: false,
            tension: 0.3,
            pointBackgroundColor: color,
            pointBorderColor: color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      };
    } else if (currentConfig.type === 'simple') {
      // Para conversion-areas que solo tiene una línea simple
      const assetsWithStats = currentTypeAssets
        .filter(asset => asset.stats &&
          typeof asset.stats.convertedVsPrevHa === 'number' &&
          typeof asset.stats.year === 'number')
        .sort((a, b) => a.stats.year - b.stats.year);

      if (assetsWithStats.length === 0) {
        return null;
      }

      const years = assetsWithStats.map(asset => asset.stats.year);
      const values = assetsWithStats.map(asset => asset.stats.convertedVsPrevHa);

      const color = currentConfig.color;

      return {
        labels: years,
        datasets: [
          {
            label: 'Convertido vs previo (ha)',
            data: values,
            borderColor: color,
            backgroundColor: transparentize(color, 0.1),
            borderWidth: 3,
            fill: false,
            tension: 0.3,
            pointBackgroundColor: color,
            pointBorderColor: color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      };
    } else if (currentConfig.type === 'multi') {
      let assetsWithStats, years, datasets; // eslint-disable-line

      if (currentGeoInfoTypeId === 'landcover-water') {
        assetsWithStats = currentTypeAssets
          .filter(asset => {
            const hasStats = asset.stats;
            const hasWaterArea = hasStats && typeof asset.stats.waterArea === 'number';
            const hasSnowArea = hasStats && typeof asset.stats.snowArea === 'number';
            const hasTotalArea = hasStats && typeof asset.stats.totalArea === 'number';
            const hasYear = hasStats && typeof asset.stats.year === 'number';
            return hasWaterArea && hasSnowArea && hasTotalArea && hasYear;
          })
          .sort((a, b) => a.stats.year - b.stats.year);

        if (assetsWithStats.length === 0) {
          return null;
        }

        years = assetsWithStats.map(asset => asset.stats.year);
        const waterValues = assetsWithStats.map(asset => asset.stats.waterArea);
        const snowValues = assetsWithStats.map(asset => asset.stats.snowArea);
        const totalValues = assetsWithStats.map(asset => asset.stats.totalArea);

        const { lines } = currentConfig;

        datasets = [
          {
            label: lines.water.label,
            data: waterValues,
            borderColor: lines.water.color,
            backgroundColor: transparentize(lines.water.color, 0.1),
            borderWidth: 2,
            fill: false,
            tension: 0.3,
            pointBackgroundColor: lines.water.color,
            pointBorderColor: lines.water.color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
          {
            label: lines.snow.label,
            data: snowValues,
            borderColor: lines.snow.color,
            backgroundColor: transparentize(lines.snow.color, 0.1),
            borderWidth: 2,
            fill: false,
            tension: 0.3,
            pointBackgroundColor: lines.snow.color,
            pointBorderColor: lines.snow.color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
          {
            label: lines.total.label,
            data: totalValues,
            borderColor: lines.total.color,
            backgroundColor: transparentize(lines.total.color, 0.1),
            borderWidth: 3,
            fill: false,
            tension: 0.3,
            pointBackgroundColor: lines.total.color,
            pointBorderColor: lines.total.color,
            pointRadius: 5,
            pointHoverRadius: 7,
          },
        ];
      } else if (currentGeoInfoTypeId === 'eii-reclass') {
        assetsWithStats = currentTypeAssets
          .filter(asset => {
            const hasStats = asset.stats;
            const hasVeryLow = hasStats && typeof asset.stats.veryLow === 'number';
            const hasLow = hasStats && typeof asset.stats.low === 'number';
            const hasMedium = hasStats && typeof asset.stats.medium === 'number';
            const hasHigh = hasStats && typeof asset.stats.high === 'number';
            const hasVeryHigh = hasStats && typeof asset.stats.veryHigh === 'number';
            const hasYear = hasStats && typeof asset.stats.year === 'number';
            return hasVeryLow && hasLow && hasMedium && hasHigh && hasVeryHigh && hasYear;
          })
          .sort((a, b) => a.stats.year - b.stats.year);

        if (assetsWithStats.length === 0) {
          return null;
        }

        years = assetsWithStats.map(asset => asset.stats.year);
        const veryLowValues = assetsWithStats.map(asset => asset.stats.veryLow);
        const lowValues = assetsWithStats.map(asset => asset.stats.low);
        const mediumValues = assetsWithStats.map(asset => asset.stats.medium);
        const highValues = assetsWithStats.map(asset => asset.stats.high);
        const veryHighValues = assetsWithStats.map(asset => asset.stats.veryHigh);

        const { lines } = currentConfig;

        datasets = [
          {
            label: lines.veryLow.label,
            data: veryLowValues,
            borderColor: lines.veryLow.color,
            backgroundColor: transparentize(lines.veryLow.color, 0.1),
            borderWidth: 2,
            fill: false,
            tension: 0.3,
            pointBackgroundColor: lines.veryLow.color,
            pointBorderColor: lines.veryLow.color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
          {
            label: lines.low.label,
            data: lowValues,
            borderColor: lines.low.color,
            backgroundColor: transparentize(lines.low.color, 0.1),
            borderWidth: 2,
            fill: false,
            tension: 0.3,
            pointBackgroundColor: lines.low.color,
            pointBorderColor: lines.low.color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
          {
            label: lines.medium.label,
            data: mediumValues,
            borderColor: lines.medium.color,
            backgroundColor: transparentize(lines.medium.color, 0.1),
            borderWidth: 2,
            fill: false,
            tension: 0.3,
            pointBackgroundColor: lines.medium.color,
            pointBorderColor: lines.medium.color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
          {
            label: lines.high.label,
            data: highValues,
            borderColor: lines.high.color,
            backgroundColor: transparentize(lines.high.color, 0.1),
            borderWidth: 2,
            fill: false,
            tension: 0.3,
            pointBackgroundColor: lines.high.color,
            pointBorderColor: lines.high.color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
          {
            label: lines.veryHigh.label,
            data: veryHighValues,
            borderColor: lines.veryHigh.color,
            backgroundColor: transparentize(lines.veryHigh.color, 0.1),
            borderWidth: 2,
            fill: false,
            tension: 0.3,
            pointBackgroundColor: lines.veryHigh.color,
            pointBorderColor: lines.veryHigh.color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ];
      }

      const chartDataResult = {
        labels: years,
        datasets,
      };

      return chartDataResult;
    }

    return null;
  }, [ currentGeoInfoTypeId, allAssetsByType ]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: currentConfig?.type === 'multi',
        position: 'top',
      },
      tooltip: {
        callbacks: {
          title: tooltipItems => `Año ${tooltipItems[0].label}`,
          label: context => {
            if (currentConfig?.type === 'bands') {
              if (context.datasetIndex === 2) { // Dataset de la media
                const dataIndex = context.dataIndex;
                const mean = context.parsed.y;
                const lowerBand = context.chart.data.datasets[0].data[dataIndex];
                const upperBand = context.chart.data.datasets[1].data[dataIndex];
                const stdDev = upperBand - mean;

                return [
                  `Media: ${mean.toFixed(2)}`,
                  `Desv. Estándar: ±${stdDev.toFixed(2)}`,
                  `Rango: ${lowerBand.toFixed(2)} - ${upperBand.toFixed(2)}`,
                ];
              }
              return null;
            } else if (currentConfig?.type === 'simple') {
              const value = context.parsed.y;
              const label = context.dataset.label;
              return `${label}: ${formatNumber(value, 2)} ha`;
            } else if (currentConfig?.type === 'multi') {
              const value = context.parsed.y;
              const label = context.dataset.label;
              return `${label}: ${formatNumber(value, 2)}`;
            }
            return null;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Año',
          font: {
            size: 12,
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        title: {
          display: true,
          text: currentConfig?.title || '',
          font: {
            size: 12,
          },
        },
        beginAtZero: false,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
  };

  if (!chartData) {
    return null;
  }

  return (
    <Box mt={2}>
      <Typography className={classes.subtitle} variant="body2" component="span">
        Evolución por año
      </Typography>
      <Box className={classes.chartContainer}>
        <ChartWrapper
          type="line"
          data={chartData}
          options={chartOptions}
        />
      </Box>
    </Box>
  );
};

GeospatialChart.propTypes = {
  allAssetsByType: PropTypes.object.isRequired,
  currentGeoInfoTypeId: PropTypes.string,
};


export { GeospatialChart };
